import { useEffect, useMemo, useState } from "react";
import Bugsnag from "@bugsnag/js";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/hooks/useAuth";
import { UserData } from "@/types/user-data";
import type { OnboardingPayload } from "@/types/onboarding";
import type { WorkspacePersona } from "@/config/workspacePersona";
import { PERSONA_LABELS } from "@/config/workspacePersona";
import {
  resolvePersonaFromHelperText,
  type PersonaResolutionConfidence as HelperConfidence,
} from "@/lib/resolvePersonaFromHelperText";
import { ARC_PLACEHOLDER, FIRST_SESSION_PROMPT, HELPER_ROLE_CONFIRMATION } from "@/config/onboardingCopy";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { AppLogo } from "@/components/ui/app-logo";
import { PrimaryButton } from "@/components/ui/PrimaryButton";
import { listDistrictNames, schoolsForDistrict } from "@/config/schools.config";
import { accountSchema, OTHER_OPTION, type AccountForm } from "@/lib/onboardingAccountSchema";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { ArrowLeft, BadgeCheck, Lock, Send, Sprout } from "lucide-react";
import AIleafGold from "@/components/ui/AIleafGold.svg";
import { isLeaderVerificationCodeValid, leaderVerificationHelperText } from "@/lib/leaderVerification";
import { normalizeEmailIdentity } from "@/lib/emailIdentity";
import { userService } from "@/services/userService";

const districtOptions = [...listDistrictNames(), OTHER_OPTION];

const STEPS = ["welcome", "role", "rolePick", "arc", "firstSession", "account"] as const;
type OnboardingStep = (typeof STEPS)[number];

export const OnboardingPage = () => {
  const { session, fillUserData, signOut, inviteApplication } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState<OnboardingStep>("welcome");
  const [loading, setLoading] = useState(false);

  const [checkIn, setCheckIn] = useState("");
  const [helperRaw, setHelperRaw] = useState("");
  const [workspacePersona, setWorkspacePersona] = useState<WorkspacePersona | null>(null);
  const [personaSource, setPersonaSource] = useState<"resolver" | "tiles" | null>(null);

  // AE-198: prefill role from an applied invite (school/district already
  // written to the profile by apply_pending_invite) — user can still change it.
  useEffect(() => {
    if (workspacePersona) return;
    if (inviteApplication?.applied && inviteApplication.workspace_persona) {
      setWorkspacePersona(inviteApplication.workspace_persona);
      setPersonaSource("tiles");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteApplication]);

  const [arcText, setArcText] = useState("");
  const [arcSkipped, setArcSkipped] = useState(false);
  const [helperConfidence, setHelperConfidence] = useState<HelperConfidence | null>(null);
  const [leaderCode, setLeaderCode] = useState("");

  const requiresLeaderVerification =
    workspacePersona === "leader" || workspacePersona === "district_leader";
  const leaderCodeValid = !requiresLeaderVerification || isLeaderVerificationCodeValid(leaderCode);

  const resolution = useMemo(() => resolvePersonaFromHelperText(helperRaw), [helperRaw]);
  const previewPersona = helperRaw.trim().length >= 2 ? resolution.persona : null;

  const form = useForm<AccountForm>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      user_name: "",
      district_name: "",
      custom_district_name: "",
      school_name: "",
      custom_school_name: "",
    },
    mode: "onSubmit",
  });

  const email = normalizeEmailIdentity(session?.user?.email ?? "");
  const [districtScope, setDistrictScope] = useState<string | null>(null);
  const selectedDistrict = form.watch("district_name");
  const schoolOptions = useMemo(
    () => (selectedDistrict ? [...schoolsForDistrict(selectedDistrict), OTHER_OPTION] : []),
    [selectedDistrict]
  );

  useEffect(() => {
    if (!email) {
      setDistrictScope(null);
      return;
    }

    let active = true;
    void userService.getDistrictScopeByEmail(email).then(
      (scope) => {
        if (active) setDistrictScope(scope);
      },
      (error) => {
        Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), (event) => {
          event.severity = "warning";
          event.context = "onboarding.getDistrictScopeByEmail";
        });
        if (active) setDistrictScope(null);
      },
    );

    return () => {
      active = false;
    };
  }, [email]);

  const lockFooter = (children: React.ReactNode) => (
    <div className="mt-6 flex items-start gap-2 rounded-xl border border-bodhi-gold/50 bg-amber-50/90 px-3 py-2.5 font-inter text-xs leading-snug text-stone-800 shadow-sm">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-bodhi-gold" aria-hidden />
      <span>{children}</span>
    </div>
  );

  const goAccount = () => setStep("account");

  const assignPersona = (p: WorkspacePersona, source: "resolver" | "tiles", conf?: HelperConfidence) => {
    setWorkspacePersona(p);
    setPersonaSource(source);
    setHelperConfidence(source === "tiles" ? "high" : conf ?? "high");
    setStep("arc");
  };

  const handleRoleSubmit = () => {
    const t = helperRaw.trim();
    if (t.length < 2) {
      toast({
        title: "Add a few words",
        description: "Share how you work, or use Skip to pick a role.",
        variant: "destructive",
      });
      return;
    }
    assignPersona(resolution.persona, "resolver", resolution.confidence);
  };

  const buildOnboardingPayload = (): OnboardingPayload => ({
    check_in: checkIn.trim() || undefined,
    helper_role_raw: helperRaw.trim() || null,
    helper_role_confidence: helperConfidence ?? undefined,
    persona_source: personaSource ?? undefined,
    arc_entry: arcSkipped ? null : arcText.trim() || null,
    arc_skipped: arcSkipped,
    first_session_completed: true,
    leader_verification_code: requiresLeaderVerification ? leaderCode.trim() : undefined,
    leader_verification_status: requiresLeaderVerification
      ? leaderCodeValid
        ? "verified"
        : "pending"
      : undefined,
    completed_at: new Date().toISOString(),
  });

  const onAccountSubmit = async (data: AccountForm) => {
    if (!session?.user || !workspacePersona) {
      toast({ title: "Missing role", description: "Please complete the role step.", variant: "destructive" });
      return;
    }
    if (requiresLeaderVerification && !leaderCodeValid) {
      toast({
        title: "Verification required",
        description: "Enter a valid leader access code or pick a different role to continue.",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    const isCustomDistrict = data.district_name === OTHER_OPTION;
    const isCustomSchool = data.school_name === OTHER_OPTION;
    const profileData: Omit<UserData, "created_at"> = {
      user_id: session.user.id,
      email,
      user_name: data.user_name,
      workspace_persona: workspacePersona,
      onboarding: buildOnboardingPayload(),
      ...(!isCustomDistrict && { district_name: data.district_name }),
      ...(isCustomDistrict && { custom_district_name: data.custom_district_name }),
      ...(!isCustomSchool && { school_name: data.school_name }),
      ...(isCustomSchool && { custom_school_name: data.custom_school_name }),
    };
    await fillUserData(profileData);
    setLoading(false);
  };

  const cardShell = (inner: React.ReactNode, onBack?: () => void) => (
    <div className="flex h-full w-full flex-col items-center overflow-y-auto px-4 py-8 md:py-12">
      <AppLogo className="mb-4 md:mb-6" />
      <div className="relative w-full max-w-lg rounded-2xl border border-stone-200/60 bg-white p-6 shadow-2xl shadow-black/20 md:p-8">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md px-2 py-1 font-inter text-xs text-stone-500 transition hover:bg-stone-100 hover:text-stone-800"
            aria-label="Go back"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Back
          </button>
        )}
        {inner}
      </div>
    </div>
  );

  if (step === "welcome") {
    return cardShell(
      <>
        <div className="mb-4 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-bodhi-gold/50 bg-bodhi-gold/5">
            <img src={AIleafGold} alt="" aria-hidden className="h-9 w-auto" />
          </div>
        </div>
        <p className="mb-1 text-center font-inter text-[10px] font-semibold uppercase tracking-[0.2em] text-bodhi-teal/80">
          Welcome
        </p>
        <h1 className="text-center font-literata text-2xl font-semibold text-[hsl(var(--bodhi-teal-dark))] md:text-3xl">
          We are so glad you are here.
        </h1>
        <p className="mb-5 text-center font-inter text-sm text-stone-600">Take a breath — we&apos;ll start wherever you are.</p>
        <Textarea
          value={checkIn}
          onChange={(e) => setCheckIn(e.target.value)}
          placeholder="What's on your mind right now? There's no wrong answer."
          className="min-h-[120px] resize-y border-bodhi-teal/25 bg-stone-50/50 font-inter text-stone-800 placeholder:italic placeholder:text-bodhi-teal/50"
        />
        <p className="mt-1 text-right font-inter text-xs italic text-bodhi-teal/70">Say as much or as little as feels right.</p>
        <PrimaryButton className="mt-6 w-full" type="button" onClick={() => setStep("role")}>
          Continue →
        </PrimaryButton>
        {lockFooter(
          "What you share here is yours. Nothing from your conversations is ever visible to your employer or institution in identifiable form."
        )}
      </>
    );
  }

  if (step === "role") {
    return cardShell(
      <>
        <p className="mb-1 text-center font-inter text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500">
          To serve you best
        </p>
        <h1 className="text-center font-literata text-2xl font-semibold text-[hsl(var(--bodhi-teal-dark))] md:text-3xl">
          Tell us about your role
        </h1>
        <p className="mb-4 text-center font-inter text-sm text-stone-600">Type your role in your own words. We&apos;ll understand it.</p>
        <div className="relative">
          <Sprout className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-bodhi-teal/70" aria-hidden />
          <Textarea
            value={helperRaw}
            onChange={(e) => setHelperRaw(e.target.value)}
            placeholder="e.g. speech-language pathologist, AP, kindergarten teacher…"
            className="min-h-[100px] resize-y border-bodhi-teal/25 bg-stone-50/50 pl-10 font-inter text-stone-800 placeholder:text-stone-400"
          />
        </div>
        {previewPersona && (
          <div className="mt-3 rounded-xl border border-emerald-200/80 bg-emerald-50/90 px-3 py-2.5 font-inter text-sm leading-snug text-emerald-950">
            ✓ {HELPER_ROLE_CONFIRMATION[previewPersona]}
          </div>
        )}
        <div className="mt-6 flex flex-col gap-2">
          <PrimaryButton type="button" className="w-full" onClick={handleRoleSubmit}>
            This is me →
          </PrimaryButton>
          <Button type="button" variant="outline" className="w-full border-stone-300" onClick={() => setStep("rolePick")}>
            Skip for now
          </Button>
        </div>
        {lockFooter(
          "Your role only personalizes navigation and tone. It is stored with your profile so we can support you appropriately."
        )}
      </>,
      () => setStep("welcome")
    );
  }

  if (step === "rolePick") {
    return cardShell(
      <>
        <p className="mb-1 text-center font-inter text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500">
          Pick what fits best
        </p>
        <h1 className="mb-4 text-center font-literata text-2xl font-semibold text-[hsl(var(--bodhi-teal-dark))] md:text-3xl">
          Which describes you?
        </h1>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(Object.keys(PERSONA_LABELS) as WorkspacePersona[]).map((p) => (
            <Button
              key={p}
              type="button"
              variant="outline"
              className="h-auto min-h-[3rem] justify-center border-bodhi-teal/30 py-3 font-inter text-sm font-medium text-stone-800 hover:bg-bodhi-teal/5"
              onClick={() => assignPersona(p, "tiles")}
            >
              {PERSONA_LABELS[p]}
            </Button>
          ))}
        </div>
        <Button type="button" variant="ghost" className="mt-4 w-full text-stone-500" onClick={() => setStep("role")}>
          Back to typing
        </Button>
      </>,
      () => setStep("role")
    );
  }

  if (step === "arc") {
    const p = workspacePersona ?? "teacher";
    return cardShell(
      <>
        <p className="mb-1 text-center font-inter text-[10px] font-semibold uppercase tracking-[0.2em] text-bodhi-teal/80">
          The start of your arc
        </p>
        <h1 className="text-center font-literata text-2xl font-semibold text-[hsl(var(--bodhi-teal-dark))] md:text-3xl">
          What brought you to this work?
        </h1>
        <p className="mb-4 text-center font-inter text-sm italic text-bodhi-teal/80">
          This becomes part of your growth story over time. Be brief or honest — there&apos;s no right answer.
        </p>
        <Textarea
          value={arcText}
          onChange={(e) => setArcText(e.target.value)}
          placeholder={ARC_PLACEHOLDER[p]}
          className="min-h-[140px] resize-y border-bodhi-teal/25 bg-stone-50/50 font-inter text-stone-800 placeholder:text-bodhi-teal/50"
        />
        <div className="mt-6 flex flex-col gap-2">
          <PrimaryButton
            type="button"
            className="w-full"
            onClick={() => {
              setArcSkipped(false);
              setStep("firstSession");
            }}
          >
            Begin →
          </PrimaryButton>
          <Button
            type="button"
            variant="outline"
            className="w-full border-bodhi-teal/30"
            onClick={() => {
              setArcSkipped(true);
              setArcText("");
              setStep("firstSession");
            }}
          >
            I&apos;ll come back to this
          </Button>
        </div>
      </>,
      () => setStep(personaSource === "tiles" ? "rolePick" : "role")
    );
  }

  if (step === "firstSession") {
    return cardShell(
      <>
        <p className="mb-1 text-center font-inter text-[10px] font-semibold uppercase tracking-[0.2em] text-bodhi-teal/80">
          Your sessions
        </p>
        <h1 className="text-center font-literata text-2xl font-semibold text-[hsl(var(--bodhi-teal-dark))] md:text-3xl">
          Let&apos;s begin
        </h1>
        <p className="mb-4 text-center font-inter text-sm text-stone-600">
          HeyBodhi meets you where you are. Here&apos;s a glimpse.
        </p>
        <div className="rounded-xl border border-stone-200 bg-stone-50/80 p-4">
          <p className="mb-3 font-inter text-[10px] font-semibold uppercase tracking-wider text-bodhi-teal/90">
            ● Your space · Preview
          </p>
          <div className="mb-4 max-w-[85%] rounded-2xl rounded-tl-sm bg-gray-100 p-3 py-2 font-inter text-sm leading-relaxed text-gray-700 shadow-sm md:p-4 md:text-base">
            {FIRST_SESSION_PROMPT}
          </div>
          <div className="flex gap-2" aria-label="Chat preview (not interactive)">
            <Input
              value=""
              readOnly
              disabled
              tabIndex={-1}
              placeholder="You'll be able to write back inside the app."
              className="rounded-2xl border-stone-200 bg-white/70 font-inter text-sm italic text-stone-500 md:text-base"
            />
            <Button
              type="button"
              size="icon"
              className="shrink-0 rounded-full bg-bodhi-teal text-white hover:bg-bodhi-teal/90"
              disabled
              tabIndex={-1}
              aria-label="Send (disabled in preview)"
            >
              <Send className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
        <PrimaryButton type="button" className="mt-6 w-full" onClick={goAccount}>
          Continue to your account →
        </PrimaryButton>
      </>,
      () => setStep("arc")
    );
  }

  return cardShell(
    <>
      <h1 className="mb-1 text-center font-literata text-2xl font-semibold text-[hsl(var(--bodhi-teal-dark))] md:text-3xl">
        Your account
      </h1>

      <p className="mb-6 text-center font-inter text-sm text-stone-600">
        Add your name, district, and school so we can finish setup.
      </p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onAccountSubmit)} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="user_name"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={loading}
                    placeholder="Your name"
                    className={cn(fieldState.error && "border-red-500 focus-visible:ring-red-500")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="rounded-lg border border-stone-100 bg-stone-50 px-3 py-2 font-inter text-sm text-stone-600">
            <span className="text-stone-500">Email</span>
            <div className="font-medium text-stone-900">{email}</div>
            {districtScope && <div className="mt-1 text-xs text-stone-500">District scope: {districtScope}</div>}
          </div>
          <FormField
            control={form.control}
            name="district_name"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>District</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(v) => {
                    field.onChange(v);
                    form.setValue("school_name", "");
                    form.setValue("custom_school_name", "");
                    if (v !== OTHER_OPTION) {
                      form.setValue("custom_district_name", "");
                    }
                  }}
                  disabled={loading}
                >
                  <FormControl>
                    <SelectTrigger className={cn(fieldState.error && "border-red-500 focus-visible:ring-red-500")}>
                      <SelectValue placeholder="Select a district" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {districtOptions.map((district) => (
                      <SelectItem key={district} value={district}>
                        {district}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.watch("district_name") === OTHER_OPTION && (
            <FormField
              control={form.control}
              name="custom_district_name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Custom district name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={loading}
                      placeholder="District name"
                      className={cn(fieldState.error && "border-red-500 focus-visible:ring-red-500")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="school_name"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>School</FormLabel>
                <Select value={field.value} onValueChange={field.onChange} disabled={loading || !selectedDistrict}>
                  <FormControl>
                    <SelectTrigger className={cn(fieldState.error && "border-red-500 focus-visible:ring-red-500")}>
                      <SelectValue placeholder={selectedDistrict ? "Select a school" : "Select a district first"} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {schoolOptions.map((school) => (
                      <SelectItem key={school} value={school}>
                        {school}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {form.watch("school_name") === OTHER_OPTION && (
            <FormField
              control={form.control}
              name="custom_school_name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Custom school name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={loading}
                      placeholder="School name"
                      className={cn(fieldState.error && "border-red-500 focus-visible:ring-red-500")}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormItem>
            <FormLabel>Your role</FormLabel>
            <Select
              value={workspacePersona ?? undefined}
              onValueChange={(v) => {
                setWorkspacePersona(v as WorkspacePersona);
                setPersonaSource("tiles");
              }}
              disabled={loading}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a role" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {(Object.keys(PERSONA_LABELS) as WorkspacePersona[]).map((p) => (
                  <SelectItem key={p} value={p}>
                    {PERSONA_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="font-inter text-[11px] leading-snug text-stone-500">
              We saved this from your earlier answer. Change it any time.
              {personaSource === "resolver" && helperRaw.trim() && (
                <span className="mt-0.5 block italic text-stone-400">
                  From: “{helperRaw.trim().slice(0, 80)}
                  {helperRaw.trim().length > 80 ? "…" : ""}”
                </span>
              )}
            </p>
          </FormItem>
          {requiresLeaderVerification && (
            <FormItem>
              <FormLabel className="flex items-center gap-1.5">
                <BadgeCheck className="h-4 w-4 text-bodhi-gold" aria-hidden />
                Leader access code
              </FormLabel>
              <FormControl>
                <Input
                  value={leaderCode}
                  onChange={(e) => setLeaderCode(e.target.value)}
                  placeholder="Enter your access code"
                  disabled={loading}
                  autoComplete="one-time-code"
                  className={cn(
                    !leaderCodeValid && leaderCode.length > 0 && "border-red-500 focus-visible:ring-red-500"
                  )}
                />
              </FormControl>
              <p className="font-inter text-[11px] leading-snug text-stone-500">{leaderVerificationHelperText()}</p>
              {!leaderCodeValid && leaderCode.length > 0 && (
                <p className="font-inter text-[11px] leading-snug text-red-600">
                  That code isn&apos;t recognized. Check with your district admin or pick a different role.
                </p>
              )}
            </FormItem>
          )}
          <PrimaryButton className="w-full" disabled={loading || !workspacePersona || !leaderCodeValid}>
            {loading ? "Saving…" : "Go to my dashboard →"}
          </PrimaryButton>
        </form>
      </Form>
      <Button variant="ghost" type="button" onClick={signOut} className="mt-3 w-full text-stone-500">
        Exit
      </Button>
    </>,
    () => setStep("firstSession")
  );
};
